import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

/**
 * Props for a MUI `Dialog` or `Drawer` that hand focus back to the element that
 * opened it once the page behind is visible again.
 *
 * MUI's focus trap returns focus to that element the moment the dialog starts
 * closing, but the modal keeps `aria-hidden` on the page until its exit
 * transition ends, so focus lands inside a hidden subtree and Chrome warns
 * *Blocked aria-hidden on an element because its descendant retained focus*.
 * The trap's own restore is turned off and focus is returned in `onExited`,
 * which the modal calls after it has taken `aria-hidden` off the page. An
 * opener that is gone by then — a row menu's item — is not refocused.
 *
 * A dialog its parent mounts only while it is shown never exits: closing
 * unmounts it. Focus then goes back once the unmount has finished, after the
 * modal's own cleanup has taken `aria-hidden` off the page.
 */
export function useFocusReturn(open: boolean) {
  const opener = useRef<HTMLElement | null>(null);

  // A layout effect runs before the focus trap's passive effect moves focus into the dialog.
  useLayoutEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      opener.current = document.activeElement;
    }
  }, [open]);

  const onExited = useCallback(() => {
    const element = opener.current;
    opener.current = null;
    if (element?.isConnected) element.focus();
  }, []);

  // Strict Mode unmounts and mounts every effect once in development, so the
  // hand-back waits a tick and runs only if the dialog did not come back.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const element = opener.current;
      if (!element) return;
      setTimeout(() => {
        if (mounted.current || !element.isConnected) return;
        if (document.activeElement && document.activeElement !== document.body) return;
        opener.current = null;
        element.focus();
      });
    };
  }, []);

  return useMemo(
    () => ({ disableRestoreFocus: true, slotProps: { transition: { onExited } } }) as const,
    [onExited],
  );
}
